import React, { useEffect, useState } from 'react';
import './PWAInstallButton.css';

interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
}

const PWAInstallButton: React.FC = () => {
  const [deferredPrompt, setDeferredPrompt] = useState<BeforeInstallPromptEvent | null>(null);
  const [showButton, setShowButton]         = useState(false);
  const [installing, setInstalling]         = useState(false);
  const [installed, setInstalled]           = useState(false);

  useEffect(() => {
    // Check if already installed
    const isInstalled =
      window.matchMedia('(display-mode: standalone)').matches ||
      (window.navigator as any).standalone === true;

    if (isInstalled) {
      setInstalled(true);
      // Clear dismissal when app is running in standalone mode
      localStorage.removeItem('pwa-install-dismissed');
      return;
    }

    // NOT installed — always show the popup on new visits

    const handler = (e: Event) => {
      e.preventDefault();
      setDeferredPrompt(e as BeforeInstallPromptEvent);
      setShowButton(true);
    };

    window.addEventListener('beforeinstallprompt', handler);

    // Listen for successful install
    window.addEventListener('appinstalled', () => {
      setInstalled(true);
      setShowButton(false);
      setDeferredPrompt(null);
    });

    return () => {
      window.removeEventListener('beforeinstallprompt', handler);
    };
  }, []);

  const handleInstall = async () => {
    if (!deferredPrompt) return;

    setInstalling(true);
    await deferredPrompt.prompt();

    const { outcome } = await deferredPrompt.userChoice;

    if (outcome === 'accepted') {
      setShowButton(false);
      setInstalled(true);
    } else {
      // User dismissed — popup will reappear on next visit
      setShowButton(false);
    }

    setDeferredPrompt(null);
    setInstalling(false);
  };

  const handleDismiss = () => {
    // Dismiss for this session only — will reappear on next visit
    setShowButton(false);
  };

  // Already installed or nothing to show
  if (installed || !showButton) return null;

  return (
    <div className="pwa-banner">
      <div className="pwa-banner-left">
        <img src="/Logo_FilMini.png" alt="FilMini" className="pwa-banner-icon" />
        <div className="pwa-banner-text">
          <span className="pwa-banner-title">FilMini</span>
          <span className="pwa-banner-sub">Add to home screen</span>
        </div>
      </div>
      <div className="pwa-banner-actions">
        <button
          className="pwa-banner-install"
          onClick={handleInstall}
          disabled={installing}
        >
          {installing ? '…' : 'Install'}
        </button>
        <button className="pwa-banner-dismiss" onClick={handleDismiss}>
          ✕
        </button>
      </div>
    </div>
  );
};

export default PWAInstallButton;
